import { useCallback, useEffect, useRef, useState } from 'react';
import { useActiveConversation } from '../components/chat/ActiveConversationContext';
import { SIDE_CHAT_FOLLOW_INTERVAL_MS, SIDE_CHAT_FOLLOW_MAX_MS, sideChatFollowStep } from './side-chat-follow';

/**
 * Re-runs `refresh` while the in-place side chat a screen handed off to is open, and once each time it
 * closes, until `watched` changes from its value at the hand-off or the cap passes (the rule is
 * `sideChatFollowStep`). Returns the function that arms it: call it beside `useOpenSideChat`.
 */
export function useSideChatFollow<T>(refresh: () => void, watched: T): () => void {
  const { chatMode } = useActiveConversation();
  const chatOpen = chatMode !== 'bubble';
  const [armedWith, setArmedWith] = useState<{ value: T } | null>(null);
  const [expired, setExpired] = useState(false);
  const wasChatOpenRef = useRef(chatOpen);
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  const watchedRef = useRef(watched);
  watchedRef.current = watched;

  const armed = armedWith !== null;
  const settled = armedWith !== null && !Object.is(armedWith.value, watched);

  useEffect(() => {
    if (!armedWith) return;
    setExpired(false);
    const timer = window.setTimeout(() => setExpired(true), SIDE_CHAT_FOLLOW_MAX_MS);
    return () => window.clearTimeout(timer);
  }, [armedWith]);

  useEffect(() => {
    const step = sideChatFollowStep({ armed, wasChatOpen: wasChatOpenRef.current, chatOpen, settled, expired });
    wasChatOpenRef.current = chatOpen;
    if (step.refreshNow) refreshRef.current();
    if (armed && !step.armed) setArmedWith(null);
    if (!step.poll) return;
    const interval = window.setInterval(() => {
      if (document.visibilityState !== 'hidden') refreshRef.current();
    }, SIDE_CHAT_FOLLOW_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [armed, chatOpen, settled, expired]);

  return useCallback(() => setArmedWith({ value: watchedRef.current }), []);
}
